import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { calculateMonthlyContributionWithFee } from "Charts/Utils/CommonChartUtils";
import { calculateMonthlyContribution, calculateMonthlyPayment, calculateTargetAmount } from "Charts/Utils/MortgageRepaymentChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlRadioButtons } from "Forms/Components/BlRadioButtons";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import { FormTextarea } from "Forms/Components/FormTextarea";
import { type HousingMortgageGoalFormType, getHousingMortgageFormSchema } from "Forms/Schemas/Goals/housingMortgageFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetHousingMortgageGoalQueryKey,
  useCreateHousingMortgageGoal,
  useGetAnalysisClients,
  useUpdateHousingMortgageGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  AnalysisClientItemRelationType,
  GoalOverviewSubType,
  type HousingMortgageGoalDto,
  HousingMortgageGoalDtoPriorityLevel,
  TimeRangeType,
} from "Infrastructure/ApiZfp/Schema";
import {
  DEFAULT_FEE_RATE,
  DEFAULT_INFLATION_RATE,
  INTEREST_RATE_AGGRESIVE,
  INTEREST_RATE_CONSERVATIVE,
  INTEREST_RATE_MORTGAGE,
} from "Infrastructure/Constants/financialConstants";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { addYears, differenceInYears, format } from "date-fns";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModelingAccordion } from "./ModelingAccordion";
import { MortgageRepaymentChart } from "./MortgageRepaymentChart";

type Props = {
  readonly onClose: () => void;
  readonly onSubTypeChange: (goalSubType: GoalOverviewSubType) => void;
  readonly editGoal?: HousingMortgageGoalDto;
  readonly goalId?: string;
};

export const HousingMortgageGoalForm = forwardRef<{}, Props>(({ onSubTypeChange, onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const { data: clientRelationsData } = useGetAnalysisClients(analysisId, versionId);
  const client = clientRelationsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client);
  const birthDate = client?.birthDate;

  const methods = useForm<HousingMortgageGoalFormType>({
    resolver: yupResolver(getHousingMortgageFormSchema(t)),
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          name: editGoal.name,
          includeInflation: editGoal.modeling.inflation?.enabled,
          includeEntryFee: editGoal.modeling.fee?.enabled,
          inflationRate: editGoal.modeling.inflation?.rate ?? 0,
          feeRate: editGoal.modeling.fee?.rate ?? 0,
          interestRate: editGoal.modeling.appreciationRate?.toString() as any,
          loanInterestRate: editGoal.data.interestRate?.toString() as any,
          goalSubType: GoalOverviewSubType.housing_mortgage,
          loanPaymentAmount: editGoal.data.loanPayment?.value.toString() as any,
          remainingPrincipal: editGoal.data.amountDue?.value.toString() as any,
          remainingYears: editGoal.data.remainingMortgageTerm?.value.toString() as any,
          desiredRemainingYears: editGoal.data.targetMortgageTerm?.value.toString() as any,
          monthlyContribution: editGoal.modeling.investmentAmount?.value.toString() as any,
          monthlyContributionWithFee: editGoal.modeling.investmentAmount?.value.toString() as any,
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
          priority: editGoal.priorityLevel === HousingMortgageGoalDtoPriorityLevel.high,
          note: editGoal.note || "",
        }
      : {
          includeInflation: true,
          includeEntryFee: true,
          inflationRate: DEFAULT_INFLATION_RATE.toString() as any,
          interestRate: INTEREST_RATE_CONSERVATIVE.toString() as any,
          feeRate: DEFAULT_INFLATION_RATE.toString() as any,
          goalSubType: GoalOverviewSubType.housing_mortgage,
          loanInterestRate: INTEREST_RATE_MORTGAGE.toString() as any,
          name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingMortgage),
          referenceStartDate: new Date(),
        },
  });

  const {
    control,
    setValue,
    watch,
    formState: { errors },
    handleSubmit,
  } = methods;

  const queryClient = useQueryClient();

  const { mutate: createHousingMortgageGoal } = useCreateHousingMortgageGoal();

  const { mutate: updateHousingMortgageGoal } = useUpdateHousingMortgageGoal();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(editGoal ? onEditGoal : onCreateGoal)();
    },
  }));

  const desiredRemainingYears = watch("desiredRemainingYears");
  const feeRate = watch("feeRate");
  const includeEntryFee = watch("includeEntryFee");
  const includeInflation = watch("includeInflation");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const loanInterestRate = watch("loanInterestRate");
  const loanPaymentAmount = watch("loanPaymentAmount");
  const monthlyContribution = watch("monthlyContribution");
  const remainingPrincipal = watch("remainingPrincipal");
  const remainingYears = watch("remainingYears");
  const targetAmount = watch("targetAmount");
  const referenceStartDate = watch("referenceStartDate");

  const clientAge = differenceInYears(referenceStartDate, birthDate ? new Date(birthDate) : referenceStartDate);

  useEffect(() => {
    if (editGoal) {
      setValue("interestRate", editGoal.modeling.appreciationRate?.toString() as any);
      return;
    }

    if (remainingYears && remainingYears >= 10) {
      setValue("interestRate", INTEREST_RATE_AGGRESIVE.toString() as any);
    } else {
      setValue("interestRate", INTEREST_RATE_CONSERVATIVE.toString() as any);
    }
  }, [setValue, remainingYears, editGoal]);

  const recalculateMonthlyContribution = useCallback(
    ({
      desiredRemainingYears,
    }: {
      readonly desiredRemainingYears: number;
    }) => {
      if (desiredRemainingYears && inflationRate && interestRate && loanInterestRate && loanPaymentAmount && remainingPrincipal) {
        const targetAmount = calculateTargetAmount({
          desiredRemainingYears,
          loanInterestRate,
          loanPaymentAmount,
          remainingPrincipal,
        });

        const monthlyContribution = calculateMonthlyContribution({
          inflationRate,
          interestRate,
          desiredRemainingYears,
          targetAmount,
        });
        const monthlyContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);

        setValue("targetAmount", targetAmount);
        setValue("monthlyContribution", monthlyContribution);
        setValue("monthlyContributionWithFee", monthlyContributionWithFee.toString() as any);
      }
    },
    [inflationRate, interestRate, loanInterestRate, loanPaymentAmount, remainingPrincipal, feeRate, setValue],
  );

  useEffect(() => {
    const monthlyContribution = calculateMonthlyContribution({
      inflationRate: includeInflation ? Number(inflationRate) : 0,
      interestRate,
      desiredRemainingYears,
      targetAmount,
    });
    const monthlyContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);
    setValue("monthlyContribution", monthlyContribution);
    setValue("monthlyContributionWithFee", monthlyContributionWithFee.toString() as any);
  }, [feeRate, inflationRate, interestRate, desiredRemainingYears, targetAmount, includeInflation, setValue]);

  useEffect(() => {
    if (remainingPrincipal && remainingYears && loanInterestRate && inflationRate) {
      const monthlyPayment = calculateMonthlyPayment({
        remainingPrincipal,
        remainingYears,
        loanInterestRate,
        inflationRate,
      });

      setValue("loanPaymentAmount", monthlyPayment.toString() as any);
    }
  }, [remainingPrincipal, remainingYears, loanInterestRate, inflationRate, setValue]);

  const onCreateGoal = (data: HousingMortgageGoalFormType) => {
    createHousingMortgageGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingMortgageGoalDtoPriorityLevel.high : HousingMortgageGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(addYears(referenceStartDate, data.desiredRemainingYears), "yyyy-MM-dd"),
            amountDue: {
              value: data.remainingPrincipal,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            loanPayment: {
              value: data.loanPaymentAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            remainingMortgageTerm: {
              value: data.remainingYears,
              type: TimeRangeType.year,
            },
            interestRate: data.loanInterestRate,
            targetMortgageTerm: {
              value: data.desiredRemainingYears,
              type: TimeRangeType.year,
            },
          },
          modeling: {
            earlyRepaymentAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            investmentAmount: {
              value: data.monthlyContributionWithFee,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!data.includeEntryFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: HousingMortgageGoalFormType) => {
    updateHousingMortgageGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingMortgageGoalDtoPriorityLevel.high : HousingMortgageGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(addYears(referenceStartDate, data.desiredRemainingYears), "yyyy-MM-dd"),
            amountDue: {
              value: data.remainingPrincipal,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            loanPayment: {
              value: data.loanPaymentAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            remainingMortgageTerm: {
              value: data.remainingYears,
              type: TimeRangeType.year,
            },
            interestRate: data.loanInterestRate,
            targetMortgageTerm: {
              value: data.desiredRemainingYears,
              type: TimeRangeType.year,
            },
          },
          modeling: {
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            investmentAmount: {
              value: data.monthlyContributionWithFee,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            fee: {
              enabled: !!data.includeEntryFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
            earlyRepaymentAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetHousingMortgageGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const renderChart = () => {
    if (
      targetAmount > 0 &&
      loanInterestRate >= 0 &&
      loanPaymentAmount > 0 &&
      remainingYears > 0 &&
      desiredRemainingYears > 0 &&
      remainingPrincipal
    ) {
      return (
        <MortgageRepaymentChart
          desiredRemainingYears={desiredRemainingYears}
          inflationRate={includeInflation ? Number(inflationRate) : 0}
          interestRate={interestRate ? Number(interestRate) : 0}
          loanInterestRate={Number(loanInterestRate)}
          loanPaymentAmount={loanPaymentAmount}
          monthlyContributionAmount={Number(monthlyContribution)}
          remainingPrincipal={Number(remainingPrincipal)}
          remainingYears={Number(remainingYears)}
          targetAmount={targetAmount}
          referenceStartDate={referenceStartDate}
        />
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Housing)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlCheckboxInput
                  control={control}
                  disabled={readOnly}
                  name="priority"
                  text={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  errors={errors}
                />
              </Grid>
              <Grid size={12}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                />
              </Grid>
              <Grid size={12}>
                <BlRadioButtons
                  control={control}
                  name="goalSubType"
                  label={t(Resources.AnalysisDetail.Goals.WhatAreYouPlanning)}
                  onChange={(goalSubType) => onSubTypeChange(goalSubType as GoalOverviewSubType)}
                  disabled={!!editGoal || readOnly}
                  codeList={[
                    {
                      code: GoalOverviewSubType.housing_property,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingProperty),
                    },
                    {
                      code: GoalOverviewSubType.housing_mortgage,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingMortgage),
                    },
                    {
                      code: GoalOverviewSubType.housing_other,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingOther),
                    },
                  ]}
                  errors={errors}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="remainingPrincipal"
                  label={t(Resources.AnalysisDetail.Goals.RemainingPrincipal)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      min: 0,
                      scale: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="remainingYears"
                  label={t(Resources.Common.RemainingYears)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      min: 1,
                      scale: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.Years)}
                    </Typography>
                  }
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="loanInterestRate"
                  label={t(Resources.AnalysisDetail.Goals.LoanInterestRate)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  maxLength={2}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      %
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      min: 1,
                      max: 20,
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  name="loanPaymentAmount"
                  label={t(Resources.AnalysisDetail.Goals.LoanPayment)}
                  errors={errors}
                  disabled
                  inputEndAdornment={
                    <Box sx={{ minWidth: 70 }}>
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                      </Typography>
                    </Box>
                  }
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                    },
                  ]}
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="desiredRemainingYears"
                  label={t(Resources.Common.RepayIn)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: "00",
                      max: remainingYears - 1,
                      min: 0,
                      scale: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.Years)}
                    </Typography>
                  }
                  onChange={(value) => {
                    const desiredRemainingYears = Number(value);
                    recalculateMonthlyContribution({ desiredRemainingYears });
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          {clientAge < 71 && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={8}>
                    <BlFormInput
                      control={control}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      errors={errors}
                      disabled
                      inputEndAdornment={
                        <Box>
                          <Typography variant="body2" color="secondary">
                            {t(Resources.Common.CZKSuffix)}
                          </Typography>
                        </Box>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 0,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={4}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      onChange={(newInterestRate) => {
                        if (
                          (includeInflation && Number(newInterestRate) === Number(inflationRate)) ||
                          (!includeInflation && Number(newInterestRate) <= 0)
                        ) {
                          setValue("interestRate", interestRate);
                          return;
                        }
                      }}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      mask={[
                        {
                          mask: Number,
                          min: 0,
                          max: 20,
                          scale: 1,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      disabled={Number(interestRate) === Number(inflationRate) || readOnly}
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      errors={errors}
                      onChange={(checked) => {
                        if (checked) {
                          const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;
                          setValue("inflationRate", newInflationRate);
                        }
                        const newMonthlyContribution = calculateMonthlyContribution({
                          targetAmount,
                          desiredRemainingYears,
                          interestRate,
                          inflationRate,
                        });
                        const monthlyContributionWithFee = calculateMonthlyContributionWithFee(newMonthlyContribution, feeRate);
                        setValue("monthlyContributionWithFee", monthlyContributionWithFee.toString() as any);
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeInflation || readOnly}
                      control={control}
                      name="inflationRate"
                      errors={errors}
                      onChange={(newInflationRate) => {
                        if (Number(newInflationRate) === Number(interestRate)) {
                          setValue("inflationRate", Number(inflationRate));
                        }
                        const monthlyContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);
                        setValue("monthlyContributionWithFee", monthlyContributionWithFee.toString() as any);
                      }}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                          max: 20,
                          scale: 1,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      disabled={readOnly}
                      name="includeEntryFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeEntryFee)}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_FEE_RATE : 0;
                        const monthlyContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, newFeeRate);

                        setValue("feeRate", newFeeRate);
                        setValue("monthlyContributionWithFee", monthlyContributionWithFee.toString() as any);
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeEntryFee || readOnly}
                      control={control}
                      name="feeRate"
                      errors={errors}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          min: 0,
                          max: 20,
                        },
                      ]}
                      onChange={(newFeeRate) => {
                        const monthlyContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, Number(newFeeRate));
                        setValue("monthlyContributionWithFee", monthlyContributionWithFee.toString() as any);
                      }}
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>{renderChart()}</Grid>
              <Grid size={12}>
                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
